/**
 * The strips a document reads under (stage 3): who saved it and where, the
 * agent's changes waiting for review, an older version read-only, and the
 * format bar of a block being edited. Versions never lose work: Restore and
 * Keep both leave every version in the history.
 */
import { useState, type KeyboardEvent } from "react";
import {
  Bold,
  ChevronDown,
  ChevronUp,
  Heading2,
  Heading3,
  History,
  Italic,
  Link2,
  List,
  Loader2,
  Pencil,
  RotateCcw,
  type LucideIcon,
} from "lucide-react";
import AgentMark from "@/app/routes/dashboard/components/icons/AgentMark";
import type { FormatAction } from "./docFormat";
import type { SavedBy } from "./docAttribution";

/** "by Workshop in <thread>" as words, with the viewer's own thread as a link. */
function ByLine({ by, onOpenThread }: { by: SavedBy; onOpenThread?: (threadId: string) => void }) {
  const thread = by.thread;
  return (
    <>
      {` by ${by.who}`}
      {thread ? (
        <>
          {" in "}
          {onOpenThread ? (
            <button type="button" className="ws-doc-thread" onClick={() => onOpenThread(thread.id)}>
              {thread.title}
            </button>
          ) : (
            thread.title
          )}
        </>
      ) : by.where ? (
        ` in ${by.where}`
      ) : null}
    </>
  );
}

/** "Saved Sep 24, 14:05 by Workshop in Strategy and universe extraction". */
export function SavedLine({
  when,
  by,
  onOpenThread,
}: {
  when: string;
  by: SavedBy | null;
  onOpenThread?: (threadId: string) => void;
}) {
  if (!when && !by) return null;
  return (
    <p className="ws-doc-saved">
      Saved{when ? ` ${when}` : ""}
      {by ? <ByLine by={by} onOpenThread={onOpenThread} /> : null}
    </p>
  );
}

/**
 * The agent made the version on screen: how many passages it changed, Keep
 * it, Restore the one before as a new version, and a walk through the
 * passages (1 of N).
 */
export function ReviewStrip({
  by,
  when,
  count,
  rewritten = false,
  index,
  version,
  canRestore,
  restoring,
  onKeep,
  onRestore,
  onStep,
}: {
  by: SavedBy;
  when: string;
  count: number;
  /** Too much changed to compare passage by passage: say it was rewritten. */
  rewritten?: boolean;
  /** The passage in view, from 0. */
  index: number;
  version: number;
  canRestore: boolean;
  restoring: boolean;
  onKeep: () => void;
  onRestore: () => void;
  onStep: (step: 1 | -1) => void;
}) {
  const actor = by.who.charAt(0).toUpperCase() + by.who.slice(1);
  const where = by.thread ? `, in ${by.thread.title}` : by.where ? `, in ${by.where}` : "";
  return (
    <section className="ws-doc-review" aria-label="The agent's changes">
      <p className="ws-doc-review-line">
        <AgentMark className="ws-doc-review-mark" aria-hidden />
        <span>
          {rewritten
            ? `${actor} rewrote this document`
            : `${actor} changed ${count} ${count === 1 ? "passage" : "passages"}`}
          {when ? ` ${when}` : ""}
          {where}.
        </span>
      </p>
      <div className="ws-doc-review-actions">
        <button type="button" className="ws-doc-btn ws-doc-btn--primary" onClick={onKeep}>
          Keep v{version}
        </button>
        {canRestore ? (
          <button
            type="button"
            className="ws-doc-btn"
            onClick={onRestore}
            disabled={restoring}
            aria-busy={restoring || undefined}
          >
            {restoring ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
            Restore v{version - 1}
          </button>
        ) : null}
        {rewritten ? null : (
          <span className="ws-doc-review-nav">
            <span className="ws-doc-review-count" aria-live="polite">
              {index + 1} of {count}
            </span>
            <button
              type="button"
              className="ws-head-icon"
              aria-label="Previous change"
              title="Previous change"
              disabled={count < 2}
              onClick={() => onStep(-1)}
            >
              <ChevronUp className="size-4" strokeWidth={1.8} aria-hidden />
            </button>
            <button
              type="button"
              className="ws-head-icon"
              aria-label="Next change"
              title="Next change"
              disabled={count < 2}
              onClick={() => onStep(1)}
            >
              <ChevronDown className="size-4" strokeWidth={1.8} aria-hidden />
            </button>
          </span>
        )}
      </div>
    </section>
  );
}

/** An older version on screen: whose and when, read-only, Restore as a new version or back. */
export function OlderVersionStrip({
  version,
  currentVersion,
  when,
  by,
  canRestore,
  restoring,
  onRestore,
  onBack,
}: {
  version: number;
  currentVersion: number;
  when: string;
  by: SavedBy | null;
  canRestore: boolean;
  restoring: boolean;
  onRestore: () => void;
  onBack: () => void;
}) {
  return (
    <section className="ws-doc-older" aria-label={`Version ${version}, read-only`}>
      <p className="ws-doc-older-line">
        <History className="ws-doc-older-mark" strokeWidth={1.8} aria-hidden />
        <span>
          You are reading V{version}
          {when ? `, saved ${when}` : ""}
          {by ? <ByLine by={by} /> : null}. It is read-only.
        </span>
      </p>
      <div className="ws-doc-older-actions">
        {canRestore ? (
          <button
            type="button"
            className="ws-doc-btn"
            onClick={onRestore}
            disabled={restoring}
            aria-busy={restoring || undefined}
          >
            {restoring ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
            ) : (
              <RotateCcw className="size-3.5" strokeWidth={1.9} aria-hidden />
            )}
            Restore as v{currentVersion + 1}
          </button>
        ) : null}
        <button type="button" className="ws-doc-link" onClick={onBack}>
          Back to V{currentVersion}
        </button>
      </div>
    </section>
  );
}

/** An unsaved edit this document was left with (another project, a reload), offered back. */
export function StashStrip({
  when,
  onRestore,
  onDiscard,
}: {
  when: string;
  onRestore: () => void;
  onDiscard: () => void;
}) {
  return (
    <section className="ws-doc-older ws-doc-stash" aria-label="An unsaved edit">
      <p className="ws-doc-older-line">
        <Pencil className="ws-doc-older-mark" strokeWidth={1.8} aria-hidden />
        <span>Your unsaved edit{when ? ` from ${when}` : ""}</span>
      </p>
      <div className="ws-doc-older-actions">
        <button type="button" className="ws-doc-btn ws-doc-btn--primary" onClick={onRestore}>
          Restore it
        </button>
        <button type="button" className="ws-doc-link" onClick={onDiscard}>
          Discard
        </button>
      </div>
    </section>
  );
}

const FORMATS: { action: FormatAction; label: string; icon: LucideIcon }[] = [
  { action: "h2", label: "Heading 2", icon: Heading2 },
  { action: "h3", label: "Heading 3", icon: Heading3 },
  { action: "bold", label: "Bold", icon: Bold },
  { action: "italic", label: "Italic", icon: Italic },
  { action: "list", label: "List", icon: List },
  { action: "link", label: "Link", icon: Link2 },
];

/**
 * The format bar of the block being edited: marks around the selection, and
 * Rewrite, which quotes the block in the thread's composer for the person to
 * say what they want. Buttons never take focus from the block.
 */
export function DocFormatBar({
  docked,
  onPress,
  onFormat,
  onRewrite,
}: {
  /** At the foot of a phone sheet, rather than floating over the block. */
  docked: boolean;
  /** A button is being pressed: on touch the block loses focus first, and must stay open. */
  onPress?: () => void;
  onFormat: (action: FormatAction) => void;
  onRewrite?: () => void;
}) {
  // One tab stop: the arrow keys (Home, End) move between the buttons.
  const [focusAt, setFocusAt] = useState(0);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key)) return;
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>("button"));
    if (items.length === 0) return;
    event.preventDefault();
    const rtl = window.getComputedStyle(event.currentTarget).direction === "rtl";
    const at = Math.max(0, items.findIndex((item) => item === document.activeElement));
    const step = (event.key === "ArrowRight" ? 1 : -1) * (rtl ? -1 : 1);
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? items.length - 1
          : (at + step + items.length) % items.length;
    setFocusAt(next);
    items[next]?.focus();
  };

  return (
    <div
      className={docked ? "ws-doc-bar ws-doc-bar--docked" : "ws-doc-bar co-dropdown-panel wb-glass-pop"}
      role="toolbar"
      aria-label="Format this block"
      aria-orientation="horizontal"
      data-doc-format-bar
      onKeyDown={onKeyDown}
    >
      {FORMATS.map(({ action, label, icon: Icon }, index) => (
        <button
          key={action}
          type="button"
          className="ws-doc-bar-btn"
          aria-label={label}
          title={label}
          tabIndex={index === focusAt ? 0 : -1}
          onFocus={() => setFocusAt(index)}
          onPointerDown={onPress}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onFormat(action)}
        >
          <Icon className="size-[15px]" strokeWidth={1.8} aria-hidden />
        </button>
      ))}
      {onRewrite ? (
        <>
          <span className="ws-doc-bar-rule" aria-hidden="true" />
          <button
            type="button"
            className="ws-doc-bar-rewrite"
            title="Quote this block in the chat and say what you want"
            tabIndex={focusAt === FORMATS.length ? 0 : -1}
            onFocus={() => setFocusAt(FORMATS.length)}
            onPointerDown={onPress}
            onMouseDown={(event) => event.preventDefault()}
            onClick={onRewrite}
          >
            <AgentMark className="size-[13px]" aria-hidden />
            Rewrite
          </button>
        </>
      ) : null}
    </div>
  );
}
